import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { CONSTANTS } from 'src/app/providers/constants';
import { catchError, EMPTY, switchMap, throwError } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PageReloadService } from 'src/app/services/page-reload/page-reload.service';
import { captureException } from '@sentry/angular';
import { toSentryError } from 'src/app/shared/utils/to-sentry-error/to-sentry-error';
import { readTokenShop } from 'src/app/shared/utils/read-token-shop/read-token-shop';
import { SHOP_HEADER } from 'src/app/interceptors/auth.interceptor';

export const shopifyInterceptor: HttpInterceptorFn = (req, next) => {
  const constants = inject(CONSTANTS);
  const authService = inject(AuthService);
  const pageReload = inject(PageReloadService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401)
        if (error.headers.has('x-shopify-retry-invalid-session-request')) {
          const staleToken = localStorage.getItem(constants.AUTH_TOKEN_KEY as string);
          const staleShop = readTokenShop(staleToken);

          localStorage.removeItem(constants.AUTH_TOKEN_KEY as string);

          if (authService.isAdmin) return throwError(() => error);

          // The Goodchart access token is gone or no longer accepted — mint a fresh one from
          // the Shopify session and replay the request with it.
          return authService.exchangeShopifyToken().pipe(
            switchMap((token) => {
              // A fresh mint always matches the store the app is embedded in right now. If it
              // disagrees with the token we just discarded, the merchant switched store mid
              // session (this tab was left open across the switch, or another tab minted a
              // token for a different store while sharing this one's localStorage). The root
              // singletons (ChartService, PlanService, ...) still hold the previous store's
              // data with nothing wired up to reset them — reload instead of replaying so the
              // app re-bootstraps clean. Safe from a loop: the reload happens after the mint
              // succeeded, so the next boot starts from a token that already matches.
              const newShop = readTokenShop(token);

              if (staleShop && newShop && staleShop.toLowerCase() !== newShop.toLowerCase()) {
                pageReload.reload();

                return EMPTY;
              }

              let headers = req.headers.set('Authorization', `Bearer ${token}`);

              if (newShop) headers = headers.set(SHOP_HEADER, newShop);

              return next(req.clone({ headers }));
            }),
            catchError((retryError) => {
              captureException(toSentryError(retryError));
              return throwError(() => retryError);
            }),
          );
        } else captureException(toSentryError(error));

      return throwError(() => error);
    }),
  );
};
